// The error set, ported one-for-one from internal/wire/wire.go.
//
// Decoders are strict, and they are strict in the same ORDER as the Go ones. A
// message with trailing bytes, an out-of-range chunk id, a pixel offset past the
// end of a chunk, an unknown verdict or an unknown click kind is an error, not a
// best-effort parse — because the two implementations only stay in step if the
// refusals line up as exactly as the accepts do.
//
// Every failure throws a WireError carrying a stable `code`. Tests assert on the
// code rather than on the message, so a reworded message is not a broken test and
// a changed refusal is.

export type WireErrorCode =
  | 'truncated'
  | 'trailing'
  | 'class'
  | 'scope'
  | 'tier'
  | 'chunk-id'
  | 'pixel-id'
  | 'offset'
  | 'pixel-count'
  | 'zero-run'
  | 'run-overflow'
  | 'too-many-records'
  | 'verdict'
  | 'beachhead-source'
  | 'missing-source'
  | 'flow-tier'
  | 'line-text'
  | 'payload-size'
  | 'duplicate-chunk'
  | 'bad-bool'
  | 'dirty-bitmap'
  | 'dirty-count'
  | 'token-len'
  | 'click-kind'
  | 'click-target'
  | 'reject-text'
  | 'tick-range'
  | 'gzip';

export class WireError extends Error {
  readonly code: WireErrorCode;

  constructor(code: WireErrorCode, message: string) {
    super(`wire: ${message}`);
    this.name = 'WireError';
    this.code = code;
  }
}

const make = (code: WireErrorCode, message: string) => () => new WireError(code, message);

export const errTruncated = make('truncated', 'message truncated');
export const errTrailing = make('trailing', 'trailing bytes after message');
export const errClass = make('class', 'wrong message class');
export const errScope = make('scope', 'unknown keyframe scope');
export const errTier = make('tier', 'unknown ephemeral tier');
export const errChunkID = make('chunk-id', 'chunk id out of range');
export const errPixelID = make('pixel-id', 'pixel id out of range');
export const errOffset = make('offset', 'pixel offset out of range for a chunk');
export const errPixelCount = make('pixel-count', 'pixel count does not match the scope');
export const errZeroRun = make('zero-run', 'zero-length run');
export const errRunOverflow = make('run-overflow', 'runs cover more pixels than the scope holds');
export const errTooManyRecords = make('too-many-records', 'more records than the count field can hold');
export const errVerdict = make('verdict', 'unknown verdict');
export const errBeachheadSource = make('beachhead-source', 'a beachhead flow must not carry a source pixel');
export const errMissingSource = make('missing-source', 'a non-beachhead flow must carry a source pixel');
export const errFlowTier = make('flow-tier', 'flows present that do not belong to the declared tier');
export const errLineText = make('line-text', 'feed line text too long');
export const errPayloadSize = make('payload-size', 'compressed payload expands past the decode limit');
export const errDuplicateChunk = make('duplicate-chunk', 'chunk appears in both the add and remove list');
export const errBadBool = make('bad-bool', 'boolean field is neither 0 nor 1');
export const errDirtyBitmap = make('dirty-bitmap', 'dirty bitmap sets a bit past the last chunk');
export const errDirtyCount = make('dirty-count', 'dirty count disagrees with the dirty bitmap');
export const errTokenLen = make('token-len', 'token length out of range');
export const errClickKind = make('click-kind', 'unknown click kind');
export const errClickTarget = make('click-target', 'click target off the board');
export const errRejectText = make('reject-text', 'reject reason too long');

/**
 * No Go counterpart: Go reads a u64 tick natively and the browser does not.
 * Rather than silently rounding a tick past 2^53 into the wrong number, the
 * decoder refuses it.
 */
export const errTickRange = make('tick-range', 'tick number is past the safe integer range');

/** A gzip stream the browser's DecompressionStream would not accept. */
export const errGzip = make('gzip', 'keyframe payload is not a readable gzip stream');

/** True when `e` is a WireError, optionally of a particular code. */
export function isWireError(e: unknown, code?: WireErrorCode): e is WireError {
  return e instanceof WireError && (code === undefined || e.code === code);
}
